import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdCheck, MdClose, MdFlag, MdPerson, MdAccessTime, MdInfo } from 'react-icons/md';

function AdminCustomPolls() {
  const [polls, setPolls] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('active');
  const [selectedPoll, setSelectedPoll] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showFlagsModal, setShowFlagsModal] = useState(false);
  const [showCloseFlagModal, setShowCloseFlagModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [closureReason, setClosureReason] = useState('');
  const [flagDetails, setFlagDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [pollsRes, tagsRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/custom-polls${activeFilter !== 'all' ? `?status=${activeFilter}` : ''}`),
          axios.get(`${API_URL}/api/tags`)
        ]);
        
        setPolls(pollsRes.data.polls);
        setTags(tagsRes.data);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pollsRes, tagsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/custom-polls${activeFilter !== 'all' ? `?status=${activeFilter}` : ''}`),
        axios.get(`${API_URL}/api/tags`)
      ]);
      
      setPolls(pollsRes.data.polls);
      setTags(tagsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedPoll) return;
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/admin/custom-polls/${selectedPoll.customPollId}/approve`);
      loadData();
      setShowApproveModal(false);
      setSelectedPoll(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to approve poll');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedPoll || !rejectionReason.trim()) return;
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/admin/custom-polls/${selectedPoll.customPollId}/reject`, {
        reason: rejectionReason
      });
      loadData();
      setShowRejectModal(false);
      setSelectedPoll(null);
      setRejectionReason('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reject poll');
    } finally {
      setSubmitting(false);
    }
  };

  const loadFlagDetails = async (poll) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/custom-polls/${poll.customPollId}/flags`);
      setFlagDetails(response.data);
      setSelectedPoll(poll);
      setShowFlagsModal(true);
    } catch (error) {
      console.error('Failed to load flags:', error);
    }
  };

  const handleApproveFlag = async () => {
    if (!selectedPoll || !closureReason.trim()) return;
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/admin/custom-polls/${selectedPoll.customPollId}/approve-flag`, {
        reason: closureReason
      });
      loadData();
      setShowCloseFlagModal(false);
      setShowFlagsModal(false);
      setSelectedPoll(null);
      setClosureReason('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to close poll');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismissFlags = async () => {
    if (!selectedPoll) return;
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/admin/custom-polls/${selectedPoll.customPollId}/dismiss-flags`);
      loadData();
      setShowFlagsModal(false);
      setSelectedPoll(null);
      setFlagDetails(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to dismiss flags');
    } finally {
      setSubmitting(false);
    }
  };

  const getTagNames = (tagIds) => {
    return tagIds?.map(id => tags.find(t => t.tagId === id)?.name).filter(Boolean) || [];
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Closed' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
      flagged: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Flagged' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Status Filter */}
      <div className="card-container mb-6">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'active', label: 'Active' },
            { key: 'flagged', label: 'Flagged' },
            { key: 'pending', label: 'Pending' },
            { key: 'closed', label: 'Closed' },
            { key: 'rejected', label: 'Rejected' },
            { key: 'all', label: 'All' }
          ].map(({ key, label }) => (
            <button
              key={key}
                onClick={() => setActiveFilter(key)}
                className={`px-4 py-2 rounded-card text-sm font-medium font-rubik transition-all ${
                  activeFilter === key
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Polls List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          </div>
        ) : polls.length === 0 ? (
          <div className="card-container text-center py-12">
            <MdInfo className="text-5xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 font-rubik">No custom polls found</h3>
          </div>
        ) : (
          <div className="space-y-4">
            {polls.map(poll => (
              <div key={poll.customPollId} className="card-container">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-3">
                    {getStatusBadge(poll.status)}
                    {poll.pollType === 'spotlight' && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold font-rubik">
                        Spotlight
                      </span>
                    )}
                    <span className="text-sm text-gray-500 font-rubik flex items-center gap-1">
                      <MdPerson className="text-base" />
                      {poll.createdByUsername}
                    </span>
                    {poll.flagCount > 0 && (
                      <span className="text-sm text-orange-600 font-rubik flex items-center gap-1">
                        <MdFlag className="text-base" />
                        {poll.flagCount} flags
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-rubik">{poll.durationDays} day · {poll.pointsSpent} pts</span>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-800 font-rubik mb-2">{poll.question}</h3>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {getTagNames(poll.tags).map((tagName, i) => (
                    <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-rubik">
                      {tagName}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 font-rubik mb-4">
                  <span><MdAccessTime className="inline mr-1" />Created: {formatDate(poll.createdAt)}</span>
                  {poll.approvedAt && <span>Approved: {formatDate(poll.approvedAt)}</span>}
                  {poll.expiresAt && <span>Expires: {formatDate(poll.expiresAt)}</span>}
                  {poll.responseCount > 0 && <span>{poll.responseCount} responses</span>}
                </div>

                {poll.rejectionReason && (
                  <div className="bg-red-50 border border-red-200 rounded-card p-2 mb-3 text-sm text-red-700 font-rubik">
                    <strong>Rejection:</strong> {poll.rejectionReason}
                  </div>
                )}

                {poll.closureReason && (
                  <div className="bg-orange-50 border border-orange-200 rounded-card p-2 mb-3 text-sm text-orange-700 font-rubik">
                    <strong>Closed:</strong> {poll.closureReason}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-3 border-t">
                  {poll.status === 'active' && (
                    <button
                      onClick={() => { setSelectedPoll(poll); setClosureReason(''); setShowCloseFlagModal(true); }}
                      className="btn-danger text-sm px-4 py-2 flex items-center gap-1"
                    >
                      <MdClose /> Close Poll
                    </button>
                  )}
                  {poll.status === 'pending' && (
                    <>
                      <button
                        onClick={() => { setSelectedPoll(poll); setShowApproveModal(true); }}
                        className="btn-primary text-sm px-4 py-2 flex items-center gap-1"
                      >
                        <MdCheck /> Activate
                      </button>
                      <button
                        onClick={() => { setSelectedPoll(poll); setShowRejectModal(true); }}
                        className="btn-danger text-sm px-4 py-2 flex items-center gap-1"
                      >
                        <MdClose /> Reject
                      </button>
                    </>
                  )}
                  {(poll.status === 'flagged' || poll.flagCount > 0) && (
                    <button
                      onClick={() => loadFlagDetails(poll)}
                      className="btn-secondary text-sm px-4 py-2 flex items-center gap-1"
                    >
                      <MdFlag /> View Flags ({poll.flagCount})
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Approve Confirmation Modal */}
      {showApproveModal && selectedPoll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-card max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 font-rubik">Approve Custom Poll?</h2>
            <p className="text-gray-600 font-rubik mb-4">
              This will activate the poll for {selectedPoll.durationDays} day(s).
            </p>
            <div className="bg-gray-50 rounded-card p-3 mb-4">
              <p className="text-sm font-medium text-gray-800 font-rubik">{selectedPoll.question}</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowApproveModal(false)} className="flex-1 btn-secondary py-2">
                Cancel
              </button>
              <button onClick={handleApprove} disabled={submitting} className="flex-1 btn-primary py-2">
                {submitting ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedPoll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-card max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 font-rubik">Reject Custom Poll</h2>
            <div className="bg-gray-50 rounded-card p-3 mb-4">
              <p className="text-sm font-medium text-gray-800 font-rubik">{selectedPoll.question}</p>
            </div>
            <p className="text-gray-600 font-rubik mb-2 text-sm">
              The user will receive their {selectedPoll.pointsSpent} points back.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="w-full px-3 py-2 border border-gray-300 rounded-card font-rubik mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button onClick={() => { setShowRejectModal(false); setRejectionReason(''); }} className="flex-1 btn-secondary py-2">
                Cancel
              </button>
              <button onClick={handleReject} disabled={submitting || !rejectionReason.trim()} className="flex-1 btn-danger py-2">
                {submitting ? 'Rejecting...' : 'Reject & Refund'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flags Detail Modal */}
      {showFlagsModal && flagDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-card max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4 font-rubik flex items-center gap-2">
              <MdFlag className="text-orange-500" /> Flag Reports
            </h2>
            <div className="bg-gray-50 rounded-card p-3 mb-4">
              <p className="text-sm font-medium text-gray-800 font-rubik">{flagDetails.question}</p>
            </div>
            
            <div className="space-y-3 mb-4">
              {flagDetails.flags.map(flag => (
                <div key={flag.flagId} className="border border-gray-200 rounded-card p-3">
                  <div className="flex justify-between items-start">
                    <span className="font-medium text-gray-800 font-rubik">{flag.username}</span>
                    <span className="text-xs text-gray-500 font-rubik">{formatDate(flag.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-600 font-rubik mt-1">{flag.reason}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-3 border-t">
              <button onClick={() => { setShowFlagsModal(false); setFlagDetails(null); }} className="flex-1 btn-secondary py-2">
                Close
              </button>
              <button onClick={handleDismissFlags} disabled={submitting} className="flex-1 btn-tertiary py-2">
                Dismiss Flags
              </button>
              <button onClick={() => setShowCloseFlagModal(true)} className="flex-1 btn-danger py-2">
                Close Poll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Poll via Flag Modal */}
      {showCloseFlagModal && selectedPoll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-card max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 font-rubik">Close Poll</h2>
            <p className="text-gray-600 font-rubik mb-2 text-sm">
              This will close the poll prematurely. The owner will see this reason.
            </p>
            <textarea
              value={closureReason}
              onChange={(e) => setClosureReason(e.target.value)}
              placeholder="Enter reason for closing..."
              className="w-full px-3 py-2 border border-gray-300 rounded-card font-rubik mb-4"
              rows={3}
            />
            <div className="flex gap-3">
              <button onClick={() => { setShowCloseFlagModal(false); setClosureReason(''); }} className="flex-1 btn-secondary py-2">
                Cancel
              </button>
              <button onClick={handleApproveFlag} disabled={submitting || !closureReason.trim()} className="flex-1 btn-danger py-2">
                {submitting ? 'Closing...' : 'Close Poll'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCustomPolls;
